import { z } from 'zod';

import type { SidebarItem } from '../../access';
import { SIDEBAR_KEY, SIDEBAR_STORED_AT_KEY, sidebarSchema } from '../../access/sidebar/sidebarItems';

export type ViewAsStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ViewAsSnapshotKey {
  live: string;
  original: string;
  isRestorable: (parked: string) => boolean;
}

const sidebarSnapshotSchema = z.array(z.object({ name: z.string() }).passthrough());

const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const isSidebarSnapshot = (parked: string): boolean => sidebarSnapshotSchema.safeParse(parseJson(parked)).success;

const anyValue = (): boolean => true;

export const VIEW_AS_SNAPSHOT_KEYS: readonly ViewAsSnapshotKey[] = [
  { live: 'token', original: 'originalToken', isRestorable: anyValue },
  { live: 'sideBar', original: 'originalSideBar', isRestorable: isSidebarSnapshot },
  { live: 'sideBarAt', original: 'originalSideBarAt', isRestorable: anyValue },
  { live: 'userId', original: 'originalUserId', isRestorable: anyValue },
  { live: 'landingPage', original: 'originalLandingPage', isRestorable: anyValue },
];

const VIEW_AS_SESSION_MARKERS = ['isViewing', 'posthogdetails'] as const;

export const announceViewAsChange = (): void => {
  window.dispatchEvent(new StorageEvent('storage', { key: 'isViewing' }));
};

export interface RestoredViewAsSession {
  landingPage: string | null;
}

export const restoreViewAsSnapshot = (storage: ViewAsStorage): RestoredViewAsSession => {
  const restorable = VIEW_AS_SNAPSHOT_KEYS.flatMap(({ live, original, isRestorable }) => {
    const parked = storage.getItem(original);
    return parked && isRestorable(parked) ? [{ live, parked }] : [];
  });

  restorable.forEach(({ live, parked }) => storage.setItem(live, parked));
  VIEW_AS_SNAPSHOT_KEYS.forEach(({ original }) => storage.removeItem(original));
  VIEW_AS_SESSION_MARKERS.forEach((key) => storage.removeItem(key));

  return { landingPage: restorable.find(({ live }) => live === 'landingPage')?.parked ?? null };
};

export interface ViewAsLayout {
  readonly sideBar?: readonly SidebarItem[];
  readonly menu?: readonly SidebarItem[];
  readonly landingPage?: string;
}

const viewAsLayoutSchema = z.object({
  sideBar: sidebarSchema.optional(),
  menu: sidebarSchema.optional(),
  landingPage: z.string().optional(),
});

export const parseViewAsLayout = (raw: unknown): ViewAsLayout | undefined => {
  const parsed = viewAsLayoutSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
};

const NO_SIDEBAR: readonly SidebarItem[] = [];

export const sidebarOfViewAs = (layout: ViewAsLayout | undefined): readonly SidebarItem[] => layout?.menu ?? layout?.sideBar ?? NO_SIDEBAR;

export interface ViewAsEntry {
  readonly token: string | undefined;
  readonly userId: string;
  readonly layout: ViewAsLayout | undefined;
  readonly now: number;
}

export const enterViewAsSession = (storage: ViewAsStorage, entry: ViewAsEntry): void => {
  VIEW_AS_SNAPSHOT_KEYS.forEach(({ live, original }) => {
    const current = storage.getItem(live);
    if (current !== null) storage.setItem(original, current);
  });
  if (entry.token) storage.setItem('token', entry.token);
  storage.setItem(SIDEBAR_KEY, JSON.stringify(sidebarOfViewAs(entry.layout)));
  storage.setItem(SIDEBAR_STORED_AT_KEY, String(entry.now));
  storage.setItem('userId', entry.userId);
  storage.setItem('landingPage', entry.layout?.landingPage ?? '');
  storage.setItem('isViewing', 'true');
};
